"use client";

import React, { useRef } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import {
    Autoplay,
    Pagination,
    Navigation,
    FreeMode,
    Mousewheel,
} from "swiper/modules";
import { getStrapiMedia } from "@/utils/api-helpers";
import BottomCurves from "../curves/bottomCurves";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { getBackgroundColor } from "@/utils/colorHelpers";

type SlideItem = {
    id: number;
    title: string;
    description: string;
    image: {
        url: string;
        alternativeText?: string | null;
    };
};

type ImageTextBannerWithSliderProps = {
    data: {
        heading: string;
        description: any;
        label: string;
        sliderTitle: string;
        media: {
            url: string;
            alternativeText: string | null;
        };
        slides: SlideItem[];
        waveBgColor: {
            waveBgColor: string;
        };
        backgroundColor: {
            backgroundColor: string;
        };
        componentPage: string;
        imagePosition: string;
    };
};

export default function ImageTextBannerWithSlider({
    data,
}: ImageTextBannerWithSliderProps) {
    const containerRef = useRef(null);
    const swiperRef = useRef<any>(null);
    const background = getBackgroundColor(
        data.backgroundColor?.backgroundColor,
    );

    return (
        <>
            {data.componentPage === "resources" && (
                <section
                    id="cdp-benefits"
                    className="relative pb-[10vw] md:pb-[12vw] lg:px-[10vw]"
                    style={{ background }}
                >
                    <div
                        className="max-w-[93rem] mx-auto flex flex-col lg:flex-row gap-8 lg:gap-16 items-center pt-4 md:pt-10"
                        ref={containerRef}
                    >
                        <div className="flex flex-col lg:flex-row lg:gap-16 items-start mb-12">
                            <div className="w-full">
                                {data.label && (
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="w-3 h-3 bg-purple rounded-full"></span>
                                        <span className="text-purple font-semibold text-sm md:text-base">
                                            {data.label}
                                        </span>
                                    </div>
                                )}

                                <h2 className="text-gray-900 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-6 uppercase tracking-tight">
                                    {data.heading}
                                </h2>

                                <div className="text-gray-600 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl">
                                    {data.description.map(
                                        (desc: any, di: number) => {
                                            if (desc.type !== "paragraph")
                                                return null;

                                            const combinedText = desc.children
                                                .filter(
                                                    (para: any) =>
                                                        para.text &&
                                                        para.text.trim() !== "",
                                                )
                                                .map((para: any) => {
                                                    const text = para.text
                                                        .replace(/\s+/g, " ")
                                                        .trim();
                                                    return para.bold
                                                        ? `<span class="textChild bold sm:text-xl lg:text-sm xl:text-base 2xl:text-2xl mb-4 font-medium">${text}</span>`
                                                        : text;
                                                })
                                                .join(" ");

                                            return (
                                                <p
                                                    key={di}
                                                    className="textChild mb-4 lg:text-xs xl:text-base 2xl:text-2xl font-light"
                                                    dangerouslySetInnerHTML={{
                                                        __html: combinedText,
                                                    }}
                                                />
                                            );
                                        },
                                    )}
                                </div>
                            </div>

                            {/* <div className="w-full h-full flex justify-center lg:justify-end">
                        <Image
                            src={getStrapiMedia(data.media.url)}
                            width={800}
                            height={650}
                            alt={
                                data.media.alternativeText || data.heading || ""
                            }
                            className=" translate-y-8 w-full h-auto object-contain"
                        />
                    </div> */}

                            {data.media?.url && (
                                <div className="w-full lg:w-2/5">
                                    <Image
                                        src={getStrapiMedia(data.media.url)}
                                        width={600}
                                        height={500}
                                        alt={
                                            data.media.alternativeText ||
                                            data.heading ||
                                            ""
                                        }
                                        className="translate-y-8 w-full h-auto object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <h3 className="text-purple font-bold text-xl sm:text-2xl md:text-3xl mb-8">
                        {data.sliderTitle}
                    </h3>
                    <div className="">
                        <Swiper
                            ref={swiperRef}
                            slidesPerView={1.2}
                            spaceBetween={16}
                            centeredSlides={false}
                            grabCursor={false}
                            freeMode={{
                                enabled: true,
                                sticky: false,
                                momentumBounce: true,
                                momentumRatio: 0.8,
                            }}
                            breakpoints={{
                                480: {
                                    slidesPerView: 1.5,
                                    spaceBetween: 16,
                                },
                                640: {
                                    slidesPerView: 2.2,
                                    spaceBetween: 20,
                                },
                                768: {
                                    slidesPerView: 2.5,
                                    spaceBetween: 20,
                                },
                                1024: {
                                    slidesPerView: 3.2,
                                    spaceBetween: 24,
                                },
                                1280: {
                                    slidesPerView: 4,
                                    spaceBetween: 24,
                                },
                            }}
                            pagination={{
                                type: "progressbar",
                                el: ".benefits-progress",
                            }}
                            mousewheel={{
                                forceToAxis: true,
                            }}
                            modules={[
                                Autoplay,
                                Pagination,
                                Navigation,
                                FreeMode,
                                Mousewheel,
                            ]}
                            className="benefits-slider !overflow-visible"
                        >
                            {data.slides?.map((slide) => (
                                <SwiperSlide
                                    key={slide.id}
                                    className="!bg-transparent"
                                >
                                    <div className="group bg-gradient-to-b from-[#8DFFFA] to-[#37DED7] rounded-3xl p-6 md:p-8 h-[320px] md:h-[350px] flex flex-col items-center justify-center text-center transition-all duration-300 hover:shadow-lg cursor-pointer">
                                        {slide.image?.url && (
                                            <div className="mb-6 transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:-translate-y-10 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                                <Image
                                                    src={getStrapiMedia(
                                                        slide.image.url,
                                                    )}
                                                    width={100}
                                                    height={100}
                                                    alt={
                                                        slide.image
                                                            .alternativeText ||
                                                        slide.title ||
                                                        ""
                                                    }
                                                    className="w-20 h-20 md:w-24 md:h-24 object-contain"
                                                />
                                            </div>
                                        )}

                                        <h4 className="text-purple font-bold text-lg md:text-xl leading-tight transition-all duration-200 ease-in-out group-hover:opacity-0 group-hover:translate-y-8 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                            {slide.title}
                                        </h4>

                                        <p className="text-purple text-sm md:text-xl leading-relaxed mt-4 opacity-0 translate-y-8 max-h-0 group-hover:opacity-100 group-hover:-translate-y-0 group-hover:max-h-[200px] transition-all duration-500 ease-in-out overflow-hidden">
                                            {slide.description}
                                        </p>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div className="benefits-progress h-1 bg-gray-50 rounded-full mt-8 mr-8 md:mr-10 lg:mr-24 overflow-hidden">
                            <div className="swiper-pagination-progressbar-fill bg-gray-50 h-full rounded-full"></div>
                        </div>
                    </div>

                    <BottomCurves
                        color={data.waveBgColor.waveBgColor}
                        direction="left"
                        className="z-10"
                    />
                </section>
            )}
            {data.componentPage === "services" && (
                <section
                    className="relative pb-[10vw] md:pb-[12vw] lg:px-[10vw]"
                    style={{ background }}
                >
                    <div
                        className="max-w-[93rem] mx-auto flex flex-col lg:flex-row gap-8 lg:gap-16 items-center pt-4 md:pt-10"
                        ref={containerRef}
                    >
                        <div
                            className={`flex min-h-[360px] item-stretch lg:items-center ${
                                data.imagePosition === "left"
                                    ? "flex-col-reverse lg:flex-row lg:gap-12 xl:gap-40"
                                    : data.imagePosition === "right"
                                      ? "flex-col-reverse lg:flex-row-reverse lg:gap-24 xl:gap-44"
                                      : ""
                            }`}
                        >
                            {data.media?.url && (
                                <div className="lg:w-full">
                                    <Image
                                        src={getStrapiMedia(data.media.url)}
                                        width={600}
                                        height={500}
                                        alt={
                                            data.media.alternativeText ||
                                            data.heading ||
                                            ""
                                        }
                                        className="translate-y-8 w-full h-auto object-contain"
                                    />
                                </div>
                            )}
                            <div className="w-full">
                                {data.label && (
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="w-3 h-3 bg-purple rounded-full"></span>
                                        <span className="text-purple font-semibold text-sm md:text-base">
                                            {data.label}
                                        </span>
                                    </div>
                                )}

                                <h2 className="text-gray-900 textChild md:max-w-[100%]">
                                    {data.heading}
                                </h2>

                                <div className="text-gray-600 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl">
                                    {data.description.map(
                                        (desc: any, di: number) => {
                                            if (desc.type !== "paragraph")
                                                return null;

                                            const combinedText = desc.children
                                                .filter(
                                                    (para: any) =>
                                                        para.text &&
                                                        para.text.trim() !== "",
                                                )
                                                .map((para: any) => {
                                                    const text = para.text
                                                        .replace(/\s+/g, " ")
                                                        .trim();
                                                    return para.bold
                                                        ? `<span class="textChild bold sm:text-xl lg:text-sm xl:text-base 2xl:text-2xl mb-4 font-medium">${text}</span>`
                                                        : text;
                                                })
                                                .join(" ");

                                            return (
                                                <p
                                                    key={di}
                                                    className="textChild mb-4 lg:text-xs xl:text-base 2xl:text-2xl font-light"
                                                    dangerouslySetInnerHTML={{
                                                        __html: combinedText,
                                                    }}
                                                />
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <h3 className="text-purple font-bold text-xl sm:text-2xl md:text-3xl mb-8 mt-20">
                        {data.sliderTitle}
                    </h3>
                    <div className="">
                        <Swiper
                            ref={swiperRef}
                            slidesPerView={1.2}
                            spaceBetween={16}
                            centeredSlides={false}
                            grabCursor={false}
                            freeMode={{
                                enabled: true,
                                sticky: false,
                                momentumBounce: true,
                                momentumRatio: 0.8,
                            }}
                            breakpoints={{
                                480: {
                                    slidesPerView: 1.5,
                                    spaceBetween: 16,
                                },
                                640: {
                                    slidesPerView: 2.2,
                                    spaceBetween: 20,
                                },
                                768: {
                                    slidesPerView: 2.5,
                                    spaceBetween: 20,
                                },
                                1024: {
                                    slidesPerView: 3.2,
                                    spaceBetween: 24,
                                },
                                1280: {
                                    slidesPerView: 4,
                                    spaceBetween: 24,
                                },
                            }}
                            pagination={{
                                type: "progressbar",
                                el: ".benefits-progress",
                            }}
                            mousewheel={{
                                forceToAxis: true,
                            }}
                            modules={[
                                Autoplay,
                                Pagination,
                                Navigation,
                                FreeMode,
                                Mousewheel,
                            ]}
                            className="benefits-slider !overflow-visible"
                        >
                            {data.slides?.map((slide) => (
                                <SwiperSlide
                                    key={slide.id}
                                    className="!bg-transparent"
                                >
                                    <div className="group bg-gradient-to-b from-[#8DFFFA] to-[#37DED7] rounded-3xl p-6 md:p-8 h-[320px] md:h-[350px] flex flex-col items-center justify-center text-center transition-all duration-300 hover:shadow-lg cursor-pointer">
                                        {slide.image?.url && (
                                            <div className="mb-6 transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:-translate-y-10 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                                <Image
                                                    src={getStrapiMedia(
                                                        slide.image.url,
                                                    )}
                                                    width={100}
                                                    height={100}
                                                    alt={
                                                        slide.image
                                                            .alternativeText ||
                                                        slide.title ||
                                                        ""
                                                    }
                                                    className="m-auto h-auto object-contain scale-100 md:scale-100 lg:scale-[140%] imageChild"
                                                />
                                            </div>
                                        )}

                                        <h4 className="text-purple font-bold text-lg md:text-xl leading-tight transition-all duration-200 ease-in-out group-hover:opacity-0 group-hover:translate-y-8 group-hover:scale-75 group-hover:h-0 group-hover:mb-0">
                                            {slide.title}
                                        </h4>

                                        <p className="text-purple text-sm md:text-xl leading-relaxed mt-4 opacity-0 translate-y-8 max-h-0 group-hover:opacity-100 group-hover:-translate-y-0 group-hover:max-h-[200px] transition-all duration-500 ease-in-out overflow-hidden">
                                            {slide.description}
                                        </p>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div className="benefits-progress h-1 bg-gray-50 rounded-full mt-8 mr-8 md:mr-10 lg:mr-24 overflow-hidden">
                            <div className="swiper-pagination-progressbar-fill bg-gray-50 h-full rounded-full"></div>
                        </div>
                    </div>

                    <BottomCurves
                        color={data.waveBgColor.waveBgColor}
                        direction="left"
                        className="z-10"
                    />
                </section>
            )}
        </>
    );
}
